/**
 * pdp — Alpine.js data component for the product detail page.
 *
 * Handles:
 *  - Native Shopify variant switching (price, availability, variant ID)
 *  - Grouped product switching (in-place DOM update, no pushState)
 *  - Colour swatch style resolution (grouped metaobject swatches + native colorOptions)
 *
 * Consumed via x-data="pdp(price, cmp)" in sections/main-product.liquid.
 * window.colorOptions is set inline in that section (requires Liquid rendering).
 */
/**
 * resolveOptionType(values, setting)
 * Determines whether a variant option group should render as 'pill' or 'dropdown'.
 *
 * auto criteria:
 *   - more than 10 values → dropdown (too many pills to scan quickly)
 *   - any value longer than 30 chars → dropdown (pills overflow on mobile)
 *   - otherwise → pill
 *
 * @param {string[]} values  - deduplicated option values for this group
 * @param {string}   setting - 'auto' | 'pill' | 'dropdown' from section settings
 * @returns {'pill'|'dropdown'}
 */
function resolveOptionType(values, setting, charThreshold) {
  if (setting === 'pill') return 'pill';
  if (setting === 'dropdown') return 'dropdown';
  // auto
  if (values.length > 10) return 'dropdown';
  if (values.some((v) => String(v).length > (charThreshold ?? 30))) return 'dropdown';
  return 'pill';
}

/**
 * Safely parses JSON content from a script tag by element ID.
 *
 * @param {string} id - DOM element ID
 * @param {*} fallback - Default return value on error or missing element
 * @returns {*}
 */
const parseJsonScript = (id, fallback = []) => {
  try {
    return JSON.parse(document.getElementById(id)?.textContent || '[]') || fallback;
  } catch (e) {
    console.error(`Failed to parse ${id}:`, e);
    return fallback;
  }
};

const pdp = (price, cmp) => {
  const {
    optionNames,
    optionValues,
    variantId,
    available,
    variants,
    soldPer,
    variantProductCodes,
    productCodePrefix,
    skuPrefix,
    hasOnlyDefaultVariant,
    variantPickerType,
    variantPickerCharThreshold,
  } = window.pdpDefaults ?? {};
  const sel = Object.fromEntries((optionNames ?? []).map((n, i) => [n, optionValues?.[i] ?? null]));

  return {
    base: price,
    cmp,
    qty: 1,
    available: available ?? true,
    variantId: variantId ?? null,
    variants: variants ?? [],
    variantProductCodes: { ...(variantProductCodes ?? {}) },
    productCodePrefix: productCodePrefix ?? skuPrefix ?? 'Product Code',
    optionNames: optionNames ?? [],
    selectedOptions: sel,
    groupedProducts: [],
    groupedOptionConfigs: [],
    crossProductOptions: [],
    swatchMap: {},
    hasOnlyDefaultVariant: hasOnlyDefaultVariant ?? false,
    variantOptions: [], // [{name, label, values}] — rebuilt after each swatch switch
    priceLoading: false, // true while fetching variant data on a grouped product switch
    activeProductUrl: window.location.pathname, // tracks the last-resolved grouped product URL
    // (not window.location.pathname — pushState removed)
    title: '',
    soldPer: soldPer ?? '',

    init() {
      this.title = this.$el.dataset.productTitle ?? '';
      if (!this.soldPer && this.$el.dataset.soldPer) {
        this.soldPer = this.$el.dataset.soldPer;
      }
      this.groupedProducts = parseJsonScript('grouped-products-json');
      this.groupedOptionConfigs = parseJsonScript('grouped-options-json');

      // Merge linked products' variant product codes into variantProductCodes map
      for (const gp of this.groupedProducts) {
        if (gp.variant_product_codes) {
          Object.assign(this.variantProductCodes, gp.variant_product_codes);
        }
        if (gp.variant_id && gp.product_code) {
          Object.assign(this.variantProductCodes, { [gp.variant_id]: gp.product_code });
        }
      }

      const curGroup = this.groupedProducts.find((g) => g.url === window.location.pathname);
      if (curGroup) {
        this.groupedOptionConfigs.forEach((opt) => {
          this.selectedOptions[opt.label] = curGroup.option_values[opt.key] ?? null;
        });
        this.variantId = curGroup.variant_id;
        this.base = curGroup.price;
        this.available = curGroup.available;
        if (curGroup.sold_per !== undefined) {
          this.soldPer = curGroup.sold_per ?? '';
        }
      }

      // Build swatchMap: { optionKey: { optionValue: hexColour } }
      for (const gp of this.groupedProducts) {
        if (!gp.swatch_values) continue;
        for (const [key, hex] of Object.entries(gp.swatch_values)) {
          const val = gp.option_values?.[key];
          if (hex && val) {
            this.swatchMap[key] ??= {};
            this.swatchMap[key][val] = hex;
          }
        }
      }

      // Identify which grouped options are truly cross-product (have at least one
      // non-null value in the linked products). Options like custom.number exist in
      // groupedOptionConfigs but their values live in native Shopify variants, not
      // product metafields — they'll be null for every linked product and must be
      // excluded from cross-product resolution and routing.
      this.crossProductOptions = this.groupedOptionConfigs.filter((opt) =>
        this.groupedProducts.some((gp) => gp.option_values?.[opt.key] != null),
      );

      // Populate the Alpine-reactive variant option picker from the page product's
      // native variants. Rebuilt after each swatch switch by refreshVariantPicker.
      this.buildVariantOptions(this.variants);

      // Wire up the product form to open the minicart instead of doing a full-page
      // POST. Uses the same $store.cart.add() flow as all other ATC buttons in the
      // theme (theme.js: cart.add → /cart/add.js → cart.show()).
      //
      // Guard: mark the form element once the listener is attached. If init() is
      // called a second time (e.g. Alpine re-initialising a nested x-data scope on
      // grouped-product pages), the listener is NOT re-added — preventing the double
      // XHR that was observed when two listeners fired from a single ATC click.
      const form = document.getElementById('product-form');
      if (form && !form._pdpAtcBound) {
        form._pdpAtcBound = true;
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          Alpine.store('cart').add(this.variantId, this.qty);
        });
      }
    },

    async resolveGroupedProduct(changedOption = null) {
      // First pass: strict match — all cross-product options must agree.
      let match = this.groupedProducts.find((g) =>
        this.crossProductOptions.every((opt) => {
          const sel = this.selectedOptions[opt.label];
          return sel == null || g.option_values[opt.key] === sel;
        }),
      );

      // Second pass: if strict match fails and we know which option triggered
      // the call, match on that option alone. This handles cases where
      // metafield values are inconsistent across products (e.g. a colour click
      // should always land on the right finish product even if the currently
      // selected dimension value doesn't exist on that product's metafield).
      if (!match && changedOption) {
        const pivot = this.crossProductOptions.find((o) => o.label.toLowerCase() === changedOption.toLowerCase());
        if (pivot) {
          const pivotVal = this.selectedOptions[changedOption];
          match = this.groupedProducts.find((g) => g.option_values[pivot.key] === pivotVal);
          if (match) {
            // Sync other cross-product selectedOptions to the matched product's
            // actual values so subsequent resolves start from a consistent state.
            this.crossProductOptions.forEach((opt) => {
              if (opt.label.toLowerCase() === changedOption.toLowerCase()) return;
              const actual = match.option_values[opt.key];
              if (actual != null) this.selectedOptions[opt.label] = actual;
            });
          }
        }
      }

      if (!match) return;

      if (match.variant_product_codes) {
        Object.assign(this.variantProductCodes, match.variant_product_codes);
      }
      if (match.variant_id && match.product_code) {
        Object.assign(this.variantProductCodes, { [match.variant_id]: match.product_code });
      }

      const urlChanged = match.url && match.url !== this.activeProductUrl;

      this.applySelection({
        variantId: match.variant_id,
        // Price is intentionally omitted when the URL changes: match.price is the
        // product minimum (linked_product.price in Liquid), not the selected variant's
        // price. The correct price is set by updateVariant() after the fetch resolves.
        // Updating with the wrong value here causes the visible price flicker.
        price: urlChanged ? null : match.price,
        compareAtPrice: urlChanged ? null : match.compare_at_price,
        available: match.available,
        sku: match.sku,
        productCode: match.product_code ?? this.variantProductCodes[match.variant_id] ?? match.sku,
        url: match.url,
        title: match.title,
        images: match.images,
        soldPer: match.sold_per ?? '',
      });

      // Only notify cross-section listeners when the product actually switches.
      if (urlChanged) {
        try {
          document.dispatchEvent(
            new CustomEvent('grouped-product:changed', {
              detail: { variantId: match.variant_id, sku: match.sku, soldPer: match.sold_per ?? '' },
            }),
          );
        } catch {
          // Swallow listener errors — they must not prevent the variant fetch below.
        }

        // Fade the price block while the correct variant price is being resolved.
        this.priceLoading = true;
        try {
          const res = await fetch(`${match.url}.js`);
          if (!res.ok) {
            this.applySelection({
              variantId: match.variant_id,
              price: match.price,
              compareAtPrice: match.compare_at_price,
              available: match.available,
              sku: match.sku,
            });
            return;
          }
          const product = await res.json();
          this.variants = product.variants || [];
          if (product.options) {
            this.optionNames = product.options.map((o) => (typeof o === 'string' ? o : o.name));
          }
          this.hasOnlyDefaultVariant =
            this.variants.length === 1 &&
            (this.variants[0]?.title === 'Default Title' ||
              (product.options?.[0]?.name === 'Title' && product.options?.[0]?.values?.[0] === 'Default Title'));

          // Rebuild the Alpine-reactive size picker for the newly-active product first
          // so selectedOptions is synchronized to the fetched product's available options
          this.refreshVariantPicker();
          this.updateVariant();
        } catch {
          // Fallback to match price if fetch fails
          this.applySelection({
            variantId: match.variant_id,
            price: match.price,
            compareAtPrice: match.compare_at_price,
            available: match.available,
            sku: match.sku,
          });
        } finally {
          this.priceLoading = false;
        }
      }
    },

    updateVariant() {
      let match = this.variants.find((v) =>
        v.options.every((val, i) => val === this.selectedOptions[this.optionNames[i]]),
      );
      if (!match && this.variants.length > 0) {
        match = this.variants[0];
        match.options.forEach((val, i) => {
          if (this.optionNames[i]) {
            this.selectedOptions[this.optionNames[i]] = val;
          }
        });
      }
      if (!match) return;

      this.applySelection({
        variantId: match.id,
        price: match.price,
        compareAtPrice: match.compare_at_price,
        available: match.available,
        sku: match.sku,
        productCode: this.variantProductCodes?.[match.id] ?? match.sku,
        // url / title / featuredImage intentionally omitted — native variant
        // switching stays on the same page product.
      });
    },

    /**
     * Single source of truth for updating reactive state and DOM after any
     * selection change — whether triggered by a grouped swatch or a native variant.
     *
     * Only the properties that are passed are applied; absent/undefined values
     * leave their corresponding state and DOM untouched.
     */
    applySelection({
      variantId,
      price,
      compareAtPrice,
      available,
      sku,
      productCode,
      url,
      title,
      images,
      soldPer,
    } = {}) {
      // ── Alpine reactive state ──────────────────────────────────────────────
      if (variantId != null) this.variantId = variantId;
      if (price != null) this.base = price;
      if (compareAtPrice != null) this.cmp = compareAtPrice;
      if (available != null) this.available = available;
      if (soldPer !== undefined) this.soldPer = soldPer;

      // ── Product Code / SKU (both paths) ───────────────────────────────────
      const skuEl = document.getElementById('product-sku');
      const code = productCode ?? sku;
      const prefix = skuEl?.dataset?.prefix || this.productCodePrefix || 'Product Code';
      if (skuEl && code != null) skuEl.textContent = `${prefix}: ${code}`;

      // ── Active product URL (grouped path only) ────────────────────────────
      // Update tracked URL so urlChanged is computed against the last-resolved
      // product on the next call — not window.location.pathname (no pushState).
      if (url) this.activeProductUrl = url;

      // ── Title (grouped path only) ──────────────────────────────────────────
      if (title) {
        const storeName = document.title.split(' · ').pop() ?? '';
        document.title = `${title} · ${storeName}`;
        this.title = title;
      }

      // ── Gallery (grouped path) — full image set ───────────────────────────
      if (images?.length) {
        document.dispatchEvent(
          new CustomEvent('grouped-product:image-change', {
            detail: { images },
          }),
        );
      }

      // ── Cross-section notification ─────────────────────────────────────────
      // NOTE: grouped-product:changed is dispatched by resolveGroupedProduct()
      // on product switches only — NOT here — to prevent spec-table and
      // product-details listeners from firing on every number/variant click.
    },

    /**
     * refreshVariantPicker()
     * Called after a swatch navigation to rebuild the Alpine-reactive size picker.
     * this.variants is already updated with the new product's data at call time.
     */
    refreshVariantPicker() {
      this.buildVariantOptions(this.variants);

      if (this.hasOnlyDefaultVariant || !this.variantOptions.length) {
        if (this.optionNames.length && this.variants[0]?.options?.length) {
          this.selectedOptions[this.optionNames[0]] = this.variants[0].options[0];
        }
        return;
      }

      // If the currently selected size doesn't exist on the new product,
      // reset to the first available value so the picker isn't in a broken state.
      this.variantOptions.forEach((opt) => {
        if (opt.values.length && !opt.values.includes(this.selectedOptions[opt.name])) {
          this.selectedOptions[opt.name] = opt.values[0];
        }
      });
    },

    /**
     * buildVariantOptions(variants)
     * Derives variantOptions from a Shopify variants array.
     * Each entry is { name, label, values } where:
     *   name  = native Shopify option name (used as key in selectedOptions)
     *   label = merchant-facing label from groupedOptionConfigs where available
     *   values = deduplicated option values in the order they appear on variants
     *
     * @param {Array} variants - Shopify product.variants array
     */
    buildVariantOptions(variants) {
      if (!variants?.length || this.hasOnlyDefaultVariant) {
        this.variantOptions = [];
        return;
      }

      // Collect unique values per option position (preserving variant order).
      const optionMaps = this.optionNames.map(() => new Set());
      variants.forEach((v) => {
        v.options.forEach((val, i) => {
          optionMaps[i]?.add(val);
        });
      });

      // Prefer the merchant-facing label from grouped option configs for
      // non-swatch options (e.g. "Dimensions" rather than native "Certified").
      // Swatch options are handled by the Liquid finish picker, not this array.
      const nonSwatchConfigs = this.groupedOptionConfigs.filter((opt) => !this.swatchMap[opt.key]);

      this.variantOptions = this.optionNames
        .map((name, i) => {
          const values = [...optionMaps[i]];
          return {
            name,
            label: nonSwatchConfigs[i]?.label ?? name,
            values,
            type: resolveOptionType(values, variantPickerType ?? 'auto', variantPickerCharThreshold ?? 30),
          };
        })
        .filter((opt) => opt.values.length > 1);
    },

    unit() {
      return this.$store.vat.mode === 'inc' ? this.base : this.base / 1.2;
    },

    selectOption(name, value) {
      this.selectedOptions[name] = value;
      if (this.crossProductOptions.some((opt) => opt.label.toLowerCase() === name.toLowerCase())) {
        this.resolveGroupedProduct(name);
      } else {
        this.updateVariant();
      }
    },

    isAvailable(optionIndex, candidate) {
      return this.variants.some((v) => {
        if (v.options[optionIndex] !== candidate) return false;
        return (
          this.optionNames.every((n, i) => i === optionIndex || v.options[i] === this.selectedOptions[n]) && v.available
        );
      });
    },

    getSwatchStyle(name, value) {
      // Grouped finish swatches — look up from swatchMap built in init()
      if (this.swatchMap) {
        const opt = this.groupedOptionConfigs.find((o) => o.label.toLowerCase() === name.toLowerCase());
        const hex = opt && this.swatchMap[opt.key]?.[value];
        if (hex) return `background-color:${hex};`;
      }
      return '';
    },
  };
};
